import type { PublicClientMessage } from '../api/CoreApi';

/**
 * Zdieľané okolo správ platformy („message of the day"). Rovnaký súbor
 * s rovnakou semantikou má aj cimpo - kontrakt je REST + MQTT, nie React
 * komponent, takže každá aplikácia si zobrazenie drží u seba.
 */

export type MessageType = PublicClientMessage['type'];

/** Typ správy → tón `Banner`-a z knižnice. */
export const MESSAGE_TONE: Record<MessageType, 'info' | 'warning' | 'error'> = {
  info: 'info',
  warning: 'warning',
  error: 'error',
};

/** Vážnosť pre zoradenie. Server posiela správy už zoradené; toto je poistka. */
const SEVERITY: Record<MessageType, number> = { error: 0, warning: 1, info: 2 };

export const bySeverity = (
  a: PublicClientMessage,
  b: PublicClientMessage,
): number =>
  SEVERITY[a.type] - SEVERITY[b.type] ||
  new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();

const ALLOWED_SCHEMES = ['http:', 'https:', 'mailto:', 'tel:'];

/**
 * `urlTransform` pre `MarkdownPreview`.
 *
 * `@uiw/react-markdown-preview` nahrádza blokovanie schém z react-markdown
 * identitou (`url => url`), takže bez tohto by `[klik](javascript:…)` v texte
 * správy vykreslila ako funkčný odkaz. Server takýto text odmieta už pri
 * zápise; toto je druhá vrstva.
 */
export const safeMarkdownUrl = (url: string): string => {
  const value = url.trim();
  if (!value) return '';
  if (value.startsWith('/') || value.startsWith('#') || value.startsWith('?')) {
    return value;
  }
  const scheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.exec(value.split('/')[0]);
  if (!scheme) return value;
  return ALLOWED_SCHEMES.includes(scheme[0].toLowerCase()) ? value : '';
};

/** Kľúč zavretia **v rámci session**. Nesie `updated_at`, takže úprava textu
 *  správu zjaví znova aj bez reloadu. Nikam sa neukladá - po F5 je správa
 *  znova vidieť, viď `MessagesBanner`. */
export const dismissKey = (message: PublicClientMessage): string =>
  `motd:${message.message_id}:${message.updated_at}`;

/** Topic, na ktorom chodí ping o zmene. Payload je len odtlačok, nie obsah. */
export const messagesTopic = (clientId: string) =>
  `system/messages/${clientId}`;
